/**
 * The transcript, and how a reply joins it (v1.98).
 *
 * The agent's answer arrives in the pieces it wrote (`message_part`), then
 * once whole (`message`). The stored reply **folds into the pieces already
 * on screen** — never shown twice, and the bubbles the person was reading never
 * collapse into one. Three cases: the reply is the parts; the reply is the
 * parts and more (shown as one more bubble); the reply is something else (the
 * stored one wins, and the parts go).
 */
import type { Schemas } from "../api/client";

export type Status = "sending" | "sent" | "joined" | "answered" | "failed";

/** A write the agent asked for: a line of its own in the thread. */
export type Request = Pick<
  Schemas["Confirmation"],
  "id" | "kind" | "summary" | "params" | "before" | "status" | "error" | "opens"
>;

/** A job a person started, as the thread shows it: a box, not a message. */
export type Job = Schemas["Job"];

export interface Line {
  key: string;
  role: "user" | "assistant";
  content: string;
  /** The stored message's id, once known: the same reply is never added twice. */
  id: number | null;
  /** A part of a reply still being written. */
  streaming?: boolean;
  /** A note from the app, or an error: no feedback flag. */
  notice?: boolean;
  error?: boolean;
  status?: Status;
  /** The line is a request, not a message; `content` is its summary. */
  request?: Request;
  /** The line is a job's box; `content` is its name. */
  job?: Job;
  /** From the earlier thread (`/history`): it was said before, so it does
   * not arrive. `cleared` is the `/clear` that took it off the screen. */
  earlier?: boolean;
  cleared?: string | null;
}

/** One line of the earlier thread, as the server places it. */
export type ThreadItem = Schemas["ThreadItem"];

let next = 0;
export const lineKey = () => `l${++next}`;

/** Every user message still waiting is answered now. */
function answered(lines: Line[]): Line[] {
  return lines.map((l) =>
    l.role === "user" && l.status && l.status !== "answered" && l.status !== "failed"
      ? { ...l, status: "answered" }
      : l,
  );
}

export function addPart(lines: Line[], content: string): Line[] {
  return [
    ...answered(lines),
    { key: lineKey(), role: "assistant", content, id: null, streaming: true },
  ];
}

export function foldReply(lines: Line[], content: string, id: number | null): Line[] {
  if (id !== null && lines.some((l) => l.id === id)) return lines;
  const now = answered(lines);
  const streamed = now
    .filter((l) => l.streaming)
    .map((l) => l.content)
    .join("\n\n");
  if (!streamed) return [...now, { key: lineKey(), role: "assistant", content, id }];

  const last = now.map((l) => Boolean(l.streaming)).lastIndexOf(true);
  const settled = now.map((l, i) =>
    l.streaming ? { ...l, streaming: false, id: i === last ? id : l.id } : l,
  );
  if (content === streamed) return settled;
  if (content.startsWith(streamed)) {
    // The turn added what the parts did not carry — today, the tool ceiling's notice.
    const extra = content.slice(streamed.length).trim();
    return extra
      ? [...settled, { key: lineKey(), role: "assistant", content: extra, id: null }]
      : settled;
  }
  // The stored reply is what was kept, so it wins.
  return [...now.filter((l) => !l.streaming), { key: lineKey(), role: "assistant", content, id }];
}

/** The run is over: whatever was still streaming is final. */
export function settle(lines: Line[]): Line[] {
  return lines.some((l) => l.streaming)
    ? lines.map((l) => (l.streaming ? { ...l, streaming: false } : l))
    : lines;
}

/** A request joins the thread once; told again, it is brought up to date. */
export function putRequest(lines: Line[], request: Request): Line[] {
  if (lines.some((l) => l.request?.id === request.id))
    return lines.map((l) => (l.request?.id === request.id ? { ...l, request } : l));
  return [
    ...lines,
    { key: lineKey(), role: "assistant", content: request.summary, id: null, request },
  ];
}

/** An answer from any screen — or none in seven days — settles the line. */
export function resolveRequest(
  lines: Line[],
  id: number,
  status: Request["status"],
  error: string | null = null,
): Line[] {
  return lines.map((l) =>
    l.request?.id === id ? { ...l, request: { ...l.request, status, error } } : l,
  );
}

/** A job's box joins the thread once; each step after that replaces it. A
    dismissed one leaves it (v2.01). */
export function putJob(lines: Line[], job: Job): Line[] {
  if (job.cleared) return lines.filter((l) => l.job?.id !== job.id);
  if (lines.some((l) => l.job?.id === job.id))
    return lines.map((l) => (l.job?.id === job.id ? { ...l, job } : l));
  return [...lines, { key: lineKey(), role: "assistant", content: job.title, id: null, job }];
}

/** `/clear`: everything goes but a job that still runs. */
export function cleared(lines: Line[]): Line[] {
  return lines.filter((l) => l.job?.state === "running");
}

/** A page of the earlier thread as lines, in the server's order. */
export function fromPage(items: readonly ThreadItem[]): Line[] {
  const lines: Line[] = [];
  for (const item of items) {
    const { message, request, job } = item;
    if (message)
      lines.push({
        key: lineKey(),
        role: message.role === "user" ? "user" : "assistant",
        content: message.content,
        id: message.id,
        earlier: true,
        cleared: message.archived ? (message.cleared_at ?? "") : null,
      });
    else if (request)
      lines.push({
        key: lineKey(),
        role: "assistant",
        content: request.summary,
        id: null,
        request: { ...request, before: request.before ?? null, error: request.error ?? null },
        earlier: true,
      });
    else if (job)
      lines.push({
        key: lineKey(),
        role: "assistant",
        content: job.title,
        id: null,
        job,
        earlier: true,
      });
  }
  return lines;
}

/**
 * `/history`: the thread becomes what the server holds — the newest page of
 * it — and only what the server does not hold yet stays from this screen: a
 * reply still being written.
 */
export function showEarlier(lines: Line[], page: readonly ThreadItem[]): Line[] {
  return [...fromPage(page), ...lines.filter((l) => l.streaming)];
}

/** An older page goes above. A line the thread already has is not added twice. */
export function addOlder(lines: Line[], page: readonly ThreadItem[]): Line[] {
  const messages = new Set(lines.map((l) => l.id).filter((id) => id !== null));
  const requests = new Set(lines.map((l) => l.request?.id));
  const jobs = new Set(lines.map((l) => l.job?.id));
  const older = fromPage(page).filter(
    (l) =>
      !(l.id !== null && messages.has(l.id)) &&
      !(l.request && requests.has(l.request.id)) &&
      !(l.job && jobs.has(l.job.id)),
  );
  return [...older, ...lines];
}

/** Where a `/clear` was: after this line, when the next message was said after it. */
export function clearedAfter(lines: readonly Line[], index: number): string | null {
  const here = lines[index]?.cleared;
  if (here === undefined || here === null) return null;
  const next = lines.slice(index + 1).find((l) => l.cleared !== undefined);
  return next && next.cleared === here ? null : here;
}

/** The index of the person's last message: 0 when they have said nothing. */
export function lastMine(lines: readonly Line[]): number {
  return Math.max(afterLastMine(lines) - 1, 0);
}

/** The index after the person's last message: 0 when they have said nothing. */
export function afterLastMine(lines: readonly Line[]): number {
  for (let i = lines.length - 1; i >= 0; i--) if (lines[i]?.role === "user") return i + 1;
  return 0;
}
